import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { sealLocal, openLocal, type SealedBlob } from "./local-vault";
import type { Message } from "@/types";

interface HeartbeatsDB extends DBSchema {
  identity: {
    key: "self";
    value: SealedBlob;
  };
  // Per-account snapshots for multi-account switching - "self" above always
  // holds whichever account is CURRENTLY active; switching accounts snapshots
  // the outgoing account's identity here (keyed by user id) before loading the
  // incoming one into "self". Same SealedBlob type, same device-bound vault
  // key underneath - these can be moved between slots as opaque blobs without
  // ever needing to decrypt them in transit.
  accountIdentities: {
    key: string; // userId
    value: SealedBlob;
  };
  // Keyed by `${conversationId}:${keyEpoch}`, not just conversationId - a
  // conversation can now have been rotated (see migration 0049), so a device
  // needs to hold onto every epoch's key it was ever given, not just the
  // latest one, to keep decrypting whichever older messages were encrypted
  // under an earlier epoch. The byConversation index exists purely so
  // purgeConversationCache (and similar full-wipe operations) can find and
  // remove every epoch's entry for a conversation in one query, without
  // needing to already know how many epochs exist or what they're numbered.
  conversationKeys: {
    key: string; // `${conversationId}:${keyEpoch}`
    value: { cacheKey: string; conversationId: string; keyEpoch: number; sealed: SealedBlob };
    indexes: { byConversation: string };
  };
  messages: {
    key: string[]; // [conversationId, messageId]
    value: { conversationId: string; messageId: string; sealed: SealedBlob };
    indexes: { byConversation: string };
  };
}

// DB_VERSION history:
//   1 - initial cache (rows stored as plain {message, plaintext}, no vault wrapping)
//   2 - added the local-vault encryption layer, changed every row's shape to
//       {..., sealed: SealedBlob}. Bumping the version forces `upgrade()` to run for
//       anyone still on v1, which wipes and recreates the stores - safe, since this is
//       purely a cache: the server has the real (encrypted) messages, and everything
//       here gets re-fetched and re-decrypted from scratch on next load anyway.
//   3 - added accountIdentities for multi-account switching. Purely additive - no
//       existing store's shape changed, so this does NOT wipe anything. Wiping the
//       identity store specifically would NOT be "just a cache miss" the way messages
//       are: it holds the private key, and losing it forces every existing user through
//       the recovery-backup flow on their next load, which the previous v1->v2 bump
//       already did once - no reason to make everyone pay that cost again for a change
//       that has nothing to do with the identity store's own shape.
//   4 - conversationKeys' key changed from plain conversationId to a composite
//       `${conversationId}:${keyEpoch}` string, to support group key rotation
//       (migration 0049) - a device now needs to hold every epoch's key it was
//       given, not just one per conversation. This DOES wipe the existing
//       conversationKeys store, same reasoning as v1->v2: it's purely a cache
//       of something re-fetchable from the server (getConversationKeys),
//       unlike the identity store.
const DB_VERSION = 4;

let dbPromise: Promise<IDBPDatabase<HeartbeatsDB>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<HeartbeatsDB>("heartbeats", DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion > 0 && oldVersion < 2) {
          // Row shape changed - old cached data can't be read by the new code path.
          // Drop it rather than leaving stale/incompatible rows lying around.
          for (const name of Array.from(db.objectStoreNames)) {
            db.deleteObjectStore(name);
          }
        }
        if (!db.objectStoreNames.contains("identity")) db.createObjectStore("identity");
        if (oldVersion > 0 && oldVersion < 4 && db.objectStoreNames.contains("conversationKeys")) {
          // Old shape (keyed by plain conversationId) can't hold more than one
          // epoch's key per conversation - drop and recreate with the new
          // composite-key shape below, same as the v1->v2 wipe: purely a
          // cache, safe to lose, re-fetched from the server on next use.
          db.deleteObjectStore("conversationKeys");
        }
        if (!db.objectStoreNames.contains("conversationKeys")) {
          const keyStore = db.createObjectStore("conversationKeys", { keyPath: "cacheKey" });
          keyStore.createIndex("byConversation", "conversationId");
        }
        if (!db.objectStoreNames.contains("messages")) {
          const messageStore = db.createObjectStore("messages", {
            keyPath: ["conversationId", "messageId"]
          });
          messageStore.createIndex("byConversation", "conversationId");
        }
        if (!db.objectStoreNames.contains("accountIdentities")) {
          db.createObjectStore("accountIdentities");
        }
      }
    });
  }
  return dbPromise;
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}
function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---------- Identity ----------
export async function saveIdentity(publicKey: string, privateKey: string) {
  const db = await getDb();
  const sealed = await sealLocal({ publicKey, privateKey });
  await db.put("identity", sealed, "self");
}

export async function loadIdentity(): Promise<{ publicKey: string; privateKey: string } | null> {
  const db = await getDb();
  const sealed = await db.get("identity", "self");
  if (!sealed) return null;
  try {
    return await openLocal<{ publicKey: string; privateKey: string }>(sealed);
  } catch {
    // Malformed/incompatible cached identity - treat as absent rather than crashing;
    // the caller (ensureIdentity) will generate and persist a fresh one.
    await db.delete("identity", "self");
    return null;
  }
}

export async function clearIdentity() {
  const db = await getDb();
  await db.delete("identity", "self");
}

// ---------- Multi-account switching ----------
// These move the SealedBlob between slots as an opaque value - never decrypted
// here, never re-encrypted, just relocated. Safe regardless of what's inside,
// since the vault key that can open it is device-bound, not account-bound (see
// local-vault.ts) - the same key opens whichever slot the blob currently sits in.

/** Snapshot whatever identity is currently active into this account's own slot,
 * so it can be restored later without re-authenticating. Call this BEFORE
 * switching away from an account, never after - there's nothing to snapshot
 * once "self" has already been overwritten by the next one. */
export async function snapshotActiveIdentity(userId: string) {
  const db = await getDb();
  const current = await db.get("identity", "self");
  if (!current) return; // nothing active yet - fine, nothing to save
  await db.put("accountIdentities", current, userId);
  // Clear the active slot immediately after preserving it - every caller uses
  // this as "I'm about to switch away from this account." Leaving the outgoing
  // identity sitting in "self" would make hasLocalIdentity()/establishIdentity()
  // for whichever account comes next incorrectly see that leftover data and
  // conclude it already has a local identity, silently reusing the wrong
  // account's keys instead of generating or restoring its own.
  await db.delete("identity", "self");
}

/** Load a previously-snapshotted account's identity into the active slot.
 * Returns false if this device has never saved that account (they'll need to
 * log in normally instead of switching). */
export async function restoreAccountIdentity(userId: string): Promise<boolean> {
  const db = await getDb();
  const saved = await db.get("accountIdentities", userId);
  if (!saved) return false;
  await db.put("identity", saved, "self");
  return true;
}

export async function forgetAccountIdentity(userId: string) {
  const db = await getDb();
  await db.delete("accountIdentities", userId);
}

export async function listSavedAccountIds(): Promise<string[]> {
  const db = await getDb();
  return db.getAllKeys("accountIdentities");
}

// ---------- Conversation keys (cached decrypted, so we don't re-run crypto_box_seal_open every load) ----------
export async function cacheConversationKey(conversationId: string, keyEpoch: number, key: Uint8Array) {
  const db = await getDb();
  const sealed = await sealLocal({ keyB64: bytesToB64(key) });
  await db.put("conversationKeys", { cacheKey: `${conversationId}:${keyEpoch}`, conversationId, keyEpoch, sealed });
}

export async function getCachedConversationKey(conversationId: string, keyEpoch: number): Promise<Uint8Array | null> {
  const db = await getDb();
  const cacheKey = `${conversationId}:${keyEpoch}`;
  const row = await db.get("conversationKeys", cacheKey);
  if (!row?.sealed) return null;
  try {
    const { keyB64 } = await openLocal<{ keyB64: string }>(row.sealed);
    return b64ToBytes(keyB64);
  } catch {
    // Corrupt/incompatible cache entry - drop it and let the caller re-fetch +
    // re-unseal from the server instead of crashing.
    await db.delete("conversationKeys", cacheKey);
    return null;
  }
}

/** The highest key_epoch this device currently has a cached (or previously
 * fetched) key for, if any - what encryptForConversation uses to know which
 * epoch to encrypt a NEW outgoing message with. Null if nothing's cached yet
 * (first message in a conversation this device hasn't loaded before). */
export async function getLatestCachedKeyEpoch(conversationId: string): Promise<number | null> {
  const db = await getDb();
  const rows = await db.getAllFromIndex("conversationKeys", "byConversation", conversationId);
  if (rows.length === 0) return null;
  return Math.max(...rows.map((r) => r.keyEpoch));
}

/**
 * Drops ONE epoch's locally-cached key for a conversation, so the next
 * getConversationKey() call for that same epoch re-fetches and re-unseals
 * from the server - never the conversation's other epochs, which is exactly
 * why this takes an explicit keyEpoch rather than wiping everything: a
 * decrypt failure on one message says its own epoch's cached key might be
 * wrong, not that every epoch this device has is suspect. Used as a
 * self-healing step when a message fails to decrypt: a cached key can end up
 * wrong (see useGetOrCreateDirect's comment for the historical bug that
 * caused this), and there is no way to distinguish "the ciphertext is
 * legitimately corrupt" from "our cached key is stale" without just trying
 * the real one.
 */
export async function evictCachedConversationKey(conversationId: string, keyEpoch: number) {
  const db = await getDb();
  await db.delete("conversationKeys", `${conversationId}:${keyEpoch}`);
}

// ---------- Decrypted messages, for instant + offline rendering ----------
export async function cacheMessage(conversationId: string, message: Message, plaintext: string | null) {
  const db = await getDb();
  const sealed = await sealLocal({ message, plaintext });
  await db.put("messages", { conversationId, messageId: message.id, sealed });
}

export async function getCachedMessages(conversationId: string): Promise<{ message: Message; plaintext: string | null }[]> {
  const db = await getDb();
  const rows = await db.getAllFromIndex("messages", "byConversation", conversationId);

  const opened: { message: Message; plaintext: string | null }[] = [];
  for (const row of rows) {
    if (!row.sealed) continue; // defensive: never let one bad row take down the whole list
    try {
      opened.push(await openLocal<{ message: Message; plaintext: string | null }>(row.sealed));
    } catch {
      // Skip corrupt entries silently - they'll be re-populated from the server
      // reconciliation pass in useDecryptedMessages anyway.
    }
  }
  return opened.sort((a, b) => a.message.created_at.localeCompare(b.message.created_at));
}

/**
 * Every cached message across every conversation, for global search. Since messages
 * are E2EE, the server never has plaintext to search - this can only ever search
 * conversations you've actually opened on this device, not your full history
 * everywhere. That's an inherent trade-off of true end-to-end encryption, not a bug.
 */
export async function getAllCachedMessages(): Promise<{ message: Message; plaintext: string | null }[]> {
  const db = await getDb();
  const rows = await db.getAll("messages");

  const opened: { message: Message; plaintext: string | null }[] = [];
  for (const row of rows) {
    if (!row.sealed) continue;
    try {
      opened.push(await openLocal<{ message: Message; plaintext: string | null }>(row.sealed));
    } catch {
      // skip corrupt entries
    }
  }
  return opened.sort((a, b) => b.message.created_at.localeCompare(a.message.created_at));
}

/**
 * Removes one message from this conversation's local cache - the actual fix
 * for a real bug: useDecryptedMessages hydrates instantly from this cache on
 * every mount/refresh (see its own docstring), then reconciles against the
 * server shortly after. Deleting a message previously only invalidated
 * React Query's in-memory cache (see useDeleteMessage), never this one - so
 * a deleted message would still be sitting here, and the very next refresh
 * (which resets the in-memory cache too) would hydrate straight from this
 * stale IndexedDB copy and briefly show the deleted message again before the
 * server reconciliation caught up and removed it a moment later. Call this
 * at the same time a message is actually deleted, not just on next full
 * cache purge.
 */
export async function removeCachedMessage(conversationId: string, messageId: string) {
  const db = await getDb();
  await db.delete("messages", [conversationId, messageId]);
}

/** Same fix as removeCachedMessage, batched for bulk-delete - one
 * transaction for every id rather than N separate ones. */
export async function removeCachedMessages(conversationId: string, messageIds: string[]) {
  const db = await getDb();
  const tx = db.transaction("messages", "readwrite");
  await Promise.all(messageIds.map((id) => tx.store.delete([conversationId, id])));
  await tx.done;
}

/**
 * Wipes every locally cached message AND the cached key for one conversation - used
 * when blocking someone deletes that conversation server-side too, so nothing lingers
 * on this device either. A blocked conversation should leave no trace anywhere.
 */
export async function purgeConversationCache(conversationId: string) {
  const db = await getDb();
  const rows = await db.getAllFromIndex("messages", "byConversation", conversationId);
  const tx = db.transaction("messages", "readwrite");
  await Promise.all(rows.map((r) => tx.store.delete([r.conversationId, r.messageId])));
  await tx.done;

  const keyRows = await db.getAllFromIndex("conversationKeys", "byConversation", conversationId);
  await Promise.all(keyRows.map((r) => db.delete("conversationKeys", r.cacheKey)));
}

/**
 * Clears only this device's locally cached messages for a conversation - never
 * touches the server, never affects the other person, never affects any other
 * device of this account. Purely "stop showing this history on THIS device from
 * now on." Deliberately leaves the cached conversation key alone (unlike
 * purgeConversationCache, used for the genuinely destructive "clear and start
 * fresh" flow) - this device still has full, normal access to the conversation,
 * it just won't instantly re-fetch a key it doesn't need to.
 */
export async function clearConversationMessages(conversationId: string) {
  const db = await getDb();
  const rows = await db.getAllFromIndex("messages", "byConversation", conversationId);
  const tx = db.transaction("messages", "readwrite");
  await Promise.all(rows.map((r) => tx.store.delete([r.conversationId, r.messageId])));
  await tx.done;
}

/** Wipe everything - called on logout so the next device/account starts clean. */
export async function clearLocalStore() {
  const db = await getDb();
  await Promise.all([db.clear("identity"), db.clear("conversationKeys"), db.clear("messages")]);
}
